import React from "react";
// Site page

// Default
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid2";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";

// Custom
import Layout from "../../layout";
import CustomInput from "../../forms/input/Input";
import CustomSearch from "../search/Search";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomChip from "../chip/Chip";
import CustomSearchBox from "../search/SearchBox";
import CustomSwitch from "../switch/Switch";
import CustomToast from "../../components/toast/CustomToast";

// React icons
import { FaRegTrashAlt } from "react-icons/fa";

// Utils
import { fetchGETRequest, fetchPOSTRequest } from "../../utils/services";
import { ToastState } from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

const ConsumerSite: React.FC = () => {
  const [searchValue, setSearchValue] = useState({
    type: "id",
    value: "",
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Details");
  const [siteData, setSiteData] = useState<{
    id: string;
    useAsLoadProfile: boolean;
    serviceCommodityType: { label: string; value: string };
    serviceType: { label: string; value: string };
    operatingHours: string;
    latitude: string;
    longitude: string;
  }>({
    id: "",
    useAsLoadProfile: false,
    serviceCommodityType: { label: "", value: "" },
    serviceType: { label: "", value: "" },
    operatingHours: "",
    latitude: "",
    longitude: "",
  });
  const [activeMeters, setActiveMeters] = useState<string[]>([]);
  const [inactiveMeters, setInactiveMeters] = useState<string[]>([]);
  const [names, setNames] = useState<string[]>([]);
  const [counter, setCounter] = useState(1);
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });
  const [linkToCustomer, setLinkToCustomer] = useState("");

  const navigate = useNavigate();

  const location = useLocation();

  const TABS = ["Details", "Coordinates", "Meters"];

  const commodityTypeOptions = [
    { label: "Electricity", value: "Electricity" },
    { label: "Water", value: "Water" },
    { label: "Gas", value: "Gas" },
  ];

  const serviceTypeOptions = [
    { label: "Commercial", value: "Commercial" },
    { label: "Residential", value: "Residential" },
  ];

  const chipColor = (status: string) => {
    if (status === "Inactive") return "#FF3B30";
    if (status === "Success") return "#9CE976";
    if (status === "Processing") return "#edb64a";
    if (status === "Pending") return "#0396FF";
  };

  const generateUniqueName = () => {
    const newName = `Site${new Date().getMilliseconds()}`;
    setNames([...names, newName]);
    setCounter(counter + 1);
    return newName;
  };

  const addRow = () => {
    setActiveMeters((prevState) => [...prevState, ""]);
  };

  const removeRow = (index: number) => {
    const updatedMeters = activeMeters.filter((_, idx) => index !== idx);
    setActiveMeters([...updatedMeters]);
  };

  const handleMeterIdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    isActive: boolean
  ) => {
    const { value } = e.target;
    if (isActive) {
      const updatedActiveMeters = [...activeMeters];
      updatedActiveMeters[index] = value;
      setActiveMeters(updatedActiveMeters);
    } else {
      const updatedInactiveMeters = [...inactiveMeters];
      updatedInactiveMeters[index] = value;
      setInactiveMeters(updatedInactiveMeters);
    }
  };

  const handleGoToMeter = (meterid: string) => {
    navigate("/meters", { state: { meterid: meterid } });
  };

  const handleSubmit = async () => {
    try {
      const requestData = {
        tenantId: {
          id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
          entityType: "TENANT",
        },
        name: generateUniqueName(),
        type: "SITE",
        label: "NY Building",
        additionalInfo: {
          UseAsLoadProfile: siteData.useAsLoadProfile,
          serviceCommodityType: siteData.serviceCommodityType?.value,
          serviceType: siteData.serviceType?.value,
          operatingHours: siteData.operatingHours,
          latitude: siteData.latitude,
          longitude: siteData.longitude,
          activeMeters: activeMeters,
        },
      };

      if (linkToCustomer) {
        let secondResponse = null;
        const response = await fetchPOSTRequest(`asset`, requestData);

        if (response) {
          secondResponse = await fetchPOSTRequest(
            `customer/${linkToCustomer}/asset/${response?.id?.id}`,
            requestData,
            true
          );
        }

        setSearchValue({ ...searchValue, value: response?.id?.id });
        setSiteData({
          id: "",
          useAsLoadProfile: false,
          serviceCommodityType: { label: "", value: "" },
          serviceType: { label: "", value: "" },
          operatingHours: "",
          latitude: "",
          longitude: "",
        });

        setActiveMeters([]);
        setLinkToCustomer("");

        if (secondResponse?.status === 200) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: response?.id?.id + " " + "Update successful!",
          });
        } else {
          setToast({
            message: response?.message,
            severity: "error",
          });
        }
      } else {
        setToast({
          message: "Link to Customer Field Required",
          severity: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error during request",
      });
    }
  };

  const handleSiteSearch = async (payload?: string) => {
    try {
      let searchTerm = "";

      if (searchValue?.value.trim()) {
        searchTerm = searchValue?.value;
      } else if (payload) {
        searchTerm = payload;
      } else {
        setToast({
          message: "Please enter a valid search value.",
          severity: "error",
        });
        return;
      }

      const response = await fetchGETRequest(`asset/${searchTerm}`, {}, true);

      if (response?.data?.additionalInfo) {
        const {
          useAsLoadProfile,
          serviceCommodityType,
          serviceType,
          operatingHours,
          latitude,
          longitude,
          activeMeters,
        } = response?.data?.additionalInfo;
        setSiteData({
          id: response?.data?.id,
          useAsLoadProfile: useAsLoadProfile,
          operatingHours: operatingHours,
          latitude: latitude,
          longitude: longitude,
          serviceCommodityType: commodityTypeOptions.find(
            (item) => item.value === serviceCommodityType
          ) || { label: "", value: "" },
          serviceType: serviceTypeOptions.find(
            (item) => item.value === serviceType
          ) || { label: "", value: "" },
        });
        setActiveMeters(activeMeters);
        setLinkToCustomer(response?.data?.customerId?.id);
        // setSearchValue({
        //   type: "id",
        //   value: "",
        // });
      } else {
        setToast({ message: "Site not found", severity: "error" });
      }
      setSearchOpen(false);
    } catch (error: any) {
      console.error("Error fetching consumer:", error.message);
    }
  };

  useEffect(() => {
    if (location?.state?.siteId) {
      setSearchValue({ ...searchValue, value: location?.state?.siteId });
      handleSiteSearch(location?.state?.siteId);
    }
  }, [location]);

  return (
    <Layout>
      <Card className="customer_title_card">
        <Typography variant="h5">Site Information</Typography>
      </Card>

      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />

      <Card
        className="customer_search_card"
        sx={{
          overflow: "visible",
          position: "relative",
        }}
      >
        <Typography variant="body1" fontWeight={600}>
          Site ID
        </Typography>

        <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
          <CustomSearchBox
            placeholder={"Enter 10 Digit"}
            width={"500px"}
            height={"40px"}
            handleSearchToggle={() => setSearchOpen(!searchOpen)}
            searchOpen={searchOpen}
            dialogBody={
              <Stack direction={"column"} gap={"20px"}>
                <CustomInput
                  label={"Type"}
                  value={searchValue?.type}
                  onChange={(e) =>
                    setSearchValue({
                      ...searchValue,
                      type: e.target.value,
                    })
                  }
                />
                <CustomInput
                  label={"Value"}
                  value={searchValue?.value}
                  onChange={(e) =>
                    setSearchValue({
                      ...searchValue,
                      value: e.target.value,
                    })
                  }
                />
                <ContainedButton
                  label={"Submit"}
                  sx={{
                    width: "100%",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                  onClick={handleSiteSearch}
                />
              </Stack>
            }
          />
          <ContainedButton
            label={"Save"}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              height: "35px",
              color: "#ffffff",
            }}
            onClick={handleSubmit}
          />
        </Stack>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <TabContext value={activeTab}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "#CACDD7",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <TabList
              onChange={(_, newValue) => setActiveTab(newValue)}
              scrollButtons={false}
              TabIndicatorProps={{
                style: {
                  backgroundColor: "##1280E7",
                  height: "3px",
                },
              }}
            >
              {TABS.map((item) => {
                return (
                  <Tab
                    label={
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={"7px"}
                        fontWeight={500}
                      >
                        {item}
                      </Box>
                    }
                    value={item}
                    className="consumer_right_card_list_tab"
                  />
                );
              })}
            </TabList>
          </Box>

          <CardContent>
            <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Stack direction={"row"} gap={"5px"}>
                  <Typography variant="body1">Status:</Typography>
                  <CustomChip
                    label={"Inactive"}
                    sx={{
                      minWidth: "auto",
                    }}
                    backgroundColor={chipColor("Inactive")}
                    color="#ffffff  "
                  />
                </Stack>

                <CustomSwitch
                  label={"Use As Load Profile"}
                  value={siteData?.useAsLoadProfile}
                  onChange={() => {
                    setSiteData({
                      ...siteData,
                      useAsLoadProfile: !siteData.useAsLoadProfile,
                    });
                  }}
                />
              </Stack>

              <Grid container spacing={2} mt={"10px"}>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomOutlinedSelect
                    label={"Service Commodity Type"}
                    options={commodityTypeOptions}
                    placeholder={"Select"}
                    name={"serviceCommodityType"}
                    value={siteData?.serviceCommodityType}
                    onChange={(newValue) =>
                      setSiteData({
                        ...siteData,
                        serviceCommodityType: newValue as {
                          label: string;
                          value: string;
                        },
                      })
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomOutlinedSelect
                    label={"Service Type"}
                    options={serviceTypeOptions}
                    placeholder={"Select"}
                    name={"serviceType"}
                    value={siteData?.serviceType}
                    onChange={(newValue) =>
                      setSiteData({
                        ...siteData,
                        serviceType: newValue as {
                          label: string;
                          value: string;
                        },
                      })
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomInput
                    label={"Operating Hours"}
                    placeholder={"Operating Hours"}
                    name={"operatingHours"}
                    value={siteData?.operatingHours}
                    onChange={(e) =>
                      setSiteData({
                        ...siteData,
                        operatingHours: e.target.value,
                      })
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomInput
                    label={"Link to Consumer"}
                    placeholder={"Link to Consumer"}
                    name={"linkToCustomer"}
                    onChange={(e) => setLinkToCustomer(e.target.value)}
                    required={true}
                    value={linkToCustomer}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomInput
                    label={"Latitude"}
                    placeholder={"Latitude"}
                    name={"latitude"}
                    value={siteData?.latitude}
                    onChange={(e) =>
                      setSiteData({
                        ...siteData,
                        latitude: e.target.value,
                      })
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomInput
                    label={"Longitude"}
                    placeholder={"Longitude"}
                    name={"longitude"}
                    onChange={(e) =>
                      setSiteData({
                        ...siteData,
                        longitude: e.target.value,
                      })
                    }
                    value={siteData?.longitude}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={TABS[2]} sx={{ padding: 0 }}>
              <Box display={"flex"} gap={"20px"}>
                <Stack width={"60%"}>
                  {activeMeters?.map((item, index) => (
                    <>
                      <Stack
                        key={index}
                        direction={"row"}
                        gap={"20px"}
                        alignItems={"center"}
                        sx={{ marginBottom: "16px" }}
                      >
                        <Typography variant="body1" fontWeight={600}>
                          Meter ID:
                        </Typography>

                        <CustomSearch
                          placeholder={"10 digits"}
                          sx={{
                            width: "400px",
                            height: "40px",
                          }}
                          value={item}
                          onChange={(e) => handleMeterIdChange(e, index, true)}
                          onClick={() => handleGoToMeter(item)}
                        />

                        <Stack direction={"row"} gap={"10px"}>
                          <ContainedButton
                            label={"Go to Meter"}
                            sx={{
                              width: "fit-content",
                              fontWeight: 600,
                              height: "35px",
                              color: "#ffffff",
                            }}
                            onClick={() => handleGoToMeter(item)}
                          />

                          <Box
                            sx={{
                              backgroundColor: "#FA6B6B",
                              width: "35px",
                              height: "35px",
                              borderRadius: "4px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                            onClick={() => removeRow(index)}
                          >
                            <UnifiedIcon
                              icon={FaRegTrashAlt}
                              color="#ffffff"
                              style={{ marginBottom: "2px" }}
                            />
                          </Box>
                        </Stack>
                      </Stack>
                    </>
                  ))}
                </Stack>
              </Box>

              <ContainedButton
                label={"Add Row"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={addRow}
              />
            </TabPanel>

            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              mt={"20px"}
            >
              {/* <ContainedButton
                label={"Install Meter"}
                
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  height: "35px",
                  color: "#ffffff",
                }}
                onClick={() => alert("coming soon...")}
              /> */}
            </Stack>
          </CardContent>
        </TabContext>
      </Card>
    </Layout>
  );
};

export default ConsumerSite;
